import { createContext, type ReactNode, useContext } from 'react';
import type { DataTableRowEventParams, SearchValues, TableData } from '@/components/custom/erp';
import type { NhaCungCap, NhaCungCapInput } from '@/types/schemas';
import { useCRUD, useForms, useFormState } from '@/hooks';
import { getTableColumns } from './cols-definition';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

interface NhaCungCapContextValue {
  // CRUD operations
  addItem: (item: NhaCungCapInput) => Promise<NhaCungCap>;
  updateItem: (id: string, item: NhaCungCapInput) => Promise<NhaCungCap>;
  deleteItem: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  data: NhaCungCap[];

  // Pagination (resolved)
  currentPage: number;
  totalItems: number;
  handlePageChange: (page: number) => void;
  handleSearchChange: (values: SearchValues) => void;
  hasFilterParams: boolean;
  tables: TableData[];

  // Form state
  selectedObj: NhaCungCap | null;
  selectedRowIndex: string | null;
  showBulkDelete: boolean | null;
  handleCloseBulkDelete: () => void;
  handleRowClick: (params: DataTableRowEventParams<NhaCungCap>) => void;
  clearSelection: () => void;
  showForm: boolean;
  showDelete: boolean;
  formMode: 'add' | 'edit' | 'view';
  isCopyMode: boolean;
  isImportDialogOpen: boolean;
  setIsImportDialogOpen: (open: boolean) => void;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  handleAddClick: () => void;
  handleEditClick: () => void;
  handleViewClick: (row?: NhaCungCap) => void;
  handleDeleteClick: () => void;
  handleCopyClick: () => void;
  handleOpenImportExcel: () => void;
}

const NhaCungCapContext = createContext<NhaCungCapContextValue | null>(null);

export function useNhaCungCapContext() {
  const context = useContext(NhaCungCapContext);
  if (!context) {
    throw new Error('useNhaCungCapContext must be used within NhaCungCapProvider');
  }
  return context;
}

export function NhaCungCapProvider({ children }: { children: ReactNode }) {
  const {
    addItem,
    updateItem,
    deleteItem,
    isLoading,
    data,
    detail,
    fetchDetail,
    currentPage,
    totalItems,
    handlePageChange,
    searchData
  } = useCRUD<NhaCungCap, NhaCungCapInput>({
    endpoint: QUERY_KEYS.NHA_CUNG_CAP,
    pageSize: 20
  });

  const { searchState, handleSearchChange, onPageChange, hasFilterParams, handleRefreshClick } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const {
    showForm,
    showDelete,
    selectedRowIndex,
    formMode,
    isCopyMode,
    showBulkDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleOpenImportExcel
  } = useForms<NhaCungCap>(fetchDetail);

  const displayData = hasFilterParams ? searchState.searchResults : data || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: 'Tất cả',
      rows: displayData,
      columns: getTableColumns(handleViewClick)
    }
  ];

  return (
    <NhaCungCapContext.Provider
      value={{
        // CRUD
        addItem,
        updateItem,
        deleteItem,
        refreshData: handleRefreshClick,
        isLoading,
        data,

        // Resolved pagination
        currentPage: resolvedCurrentPage,
        totalItems: resolvedTotalItems,
        handlePageChange: onPageChange,

        handleSearchChange,
        hasFilterParams,
        tables,

        selectedObj: detail,
        selectedRowIndex,
        showBulkDelete,
        handleCloseBulkDelete,
        handleRowClick,
        clearSelection,
        showForm,
        showDelete,
        formMode,
        isCopyMode,
        isImportDialogOpen,
        setIsImportDialogOpen,
        handleCloseForm,
        handleCloseDelete,
        handleAddClick,
        handleEditClick,
        handleViewClick,
        handleDeleteClick,
        handleCopyClick,
        handleOpenImportExcel
      }}
    >
      {children}
    </NhaCungCapContext.Provider>
  );
}
